import { useCallback } from "react";

import { useIsAuthenticated } from "@azure/msal-react";
import { useParams, usePathname } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { BreadcrumbsItem } from "~/bff/types/BreadcrumbsItem";
import {
  DataLayerData,
  EventsNames,
  EventTypes,
  NewDataLayerDataTypes,
  PlaceApiProvider,
  StoreSelectorType,
} from "~/constants/data-layer";
import { Routes } from "~/constants/request";
import { getCountryCode } from "~/helpers/country/get-country-code";
import {
  getPageCategoriesTitles,
  removeLocaleFromPath,
} from "~/helpers/get-page-categories-titles";
import { pushDataLayer } from "~/helpers/push-data-layer";
import { getPageNameByRoute } from "~/helpers/store-selector/get-page-name-by-route";
import { useIsEnabled as useIsLoqateSearchEnabled } from "~/hooks/loqate/use-is-enabled";
import { Nullable } from "~/types/general.types";

export const useGaEvents = (): {
  sendOpenStoreSelectorGAEvent: (
    breadcrumbs: Nullable<BreadcrumbsItem>[],
    event?: {
      [DataLayerData.event]: EventsNames;
      [DataLayerData.storeSelectorType]?: StoreSelectorType;
    },
  ) => void;
  getStoreSearchGAEventData: (
    breadcrumbs: Nullable<BreadcrumbsItem>[] | undefined,
  ) => NewDataLayerDataTypes;
} => {
  const isUserAuthenticated = useIsAuthenticated();
  const { locale } = useParams<GlobalPathParams>();
  const country = getCountryCode(locale);
  const isLoqateSearchEnabled = useIsLoqateSearchEnabled();
  const pathname = usePathname();

  const sendOpenStoreSelectorGAEvent = useCallback<
    (
      breadcrumbs: Nullable<BreadcrumbsItem>[],
      event?: {
        [DataLayerData.event]: EventsNames;
        [DataLayerData.storeSelectorType]?: StoreSelectorType;
      },
    ) => void
  >(
    (breadcrumbs, event) => {
      const isPdpPage = pathname.startsWith(`/${locale}/p/`);
      if (isPdpPage) {
        return;
      }
      const { pageCategory } = getPageCategoriesTitles(breadcrumbs);
      const dataForGoogleAnalytics = {
        [DataLayerData.event]:
          event?.[DataLayerData.event] || EventsNames.storeSelectorOpen,
        [DataLayerData.siteCountry]: country,
        [DataLayerData.type]: EventTypes.storeSelector,
        [DataLayerData.pageCategory]:
          pageCategory || getPageNameByRoute(removeLocaleFromPath(pathname)),
        [DataLayerData.loggedInStatus]: isUserAuthenticated,
        [DataLayerData.source]: isLoqateSearchEnabled
          ? PlaceApiProvider.loqate
          : PlaceApiProvider.google,
        ...(event?.[DataLayerData.storeSelectorType] && {
          [DataLayerData.storeSelectorType]:
            event?.[DataLayerData.storeSelectorType],
        }),
      };

      pushDataLayer({ ...dataForGoogleAnalytics });
    },
    [country, isLoqateSearchEnabled, isUserAuthenticated, locale, pathname],
  );

  const getStoreSearchGAEventData = useCallback(
    (
      breadcrumbs: Nullable<BreadcrumbsItem>[] | undefined,
    ): NewDataLayerDataTypes => {
      const { pageCategory } = getPageCategoriesTitles(breadcrumbs);
      const isShoppingBagPage =
        removeLocaleFromPath(pathname) === Routes.SHOPPING_BAG;

      return {
        [DataLayerData.event]: EventsNames.storeSelectorSearch,
        [DataLayerData.siteCountry]: country,
        [DataLayerData.type]: EventTypes.storeSelector,
        [DataLayerData.pageCategory]:
          pageCategory || getPageNameByRoute(removeLocaleFromPath(pathname)),
        [DataLayerData.loggedInStatus]: isUserAuthenticated,
        [DataLayerData.source]: isLoqateSearchEnabled
          ? PlaceApiProvider.loqate
          : PlaceApiProvider.google,
        ...(isShoppingBagPage && {
          [DataLayerData.storeSelectorType]: StoreSelectorType.shoppingBagCTA,
        }),
      };
    },
    [country, isLoqateSearchEnabled, isUserAuthenticated, pathname],
  );

  return {
    sendOpenStoreSelectorGAEvent,
    getStoreSearchGAEventData,
  };
};
